/*
  Таблица находок радара — §3.9.

  🔴 Экран радара стал таблицей 22.09.2026 по решению владельца; до этого
  конституция §3.9.4 таблицу прямо запрещала («радар не показывает данные и не
  предлагает разобраться»), и раздел правлен вместе с этим кодом. Причина
  правки видна на прежнем экране: девять находок шли девятью одинаковыми
  карточками во всю ширину, горящее и возможности вперемешку по убыванию суммы,
  и отличить одно от другого можно было только чтением мелкой метки. Кнопка
  «Открыть карточку счёта» повторялась девять раз.

  Порядок колонок задан владельцем: срочность, что нашли, почему, сумма - и
  дальше остальное. Причина стоит колонкой, а не под значком «как?»: она
  читается сразу, без наведения.

  Компонент ничего не грузит и ничего не сортирует: порядок строк задаёт
  сервер (срочность важнее суммы), он же отбирает по срочности и режет страницы.
*/

import { Link } from 'react-router-dom'

import { money } from '../api/format.js'
import { Tag } from './Common.jsx'
import PageBar from './PageBar.jsx'

export default function RadarTable({ rows, bar }) {
  // 21.10.2026: находок по крупному портфелю десятки и сотни - страницу режет сервер
  // (`RadarFindings.jsx`), здесь - строки страницы и общая панель.
  return (
    <>
      <table className="table table--fixed table--radar">
        <thead>
          <tr>
            <th>Срочность</th>
            <th>Что нашли</th>
            <th>Почему</th>
            <th className="table__num">Сумма</th>
            <th>Счёт и дело</th>
            <th>Срок</th>
            <th>Что делать</th>
          </tr>
        </thead>
        <tbody>
          {/*
            Ключ считается от документа, а не от счёта (правка 09.09.2026 вместе
            с находкой об индексации). У счёта бывает несколько исполнительных
            документов, и по каждому своя находка: ключ от счёта совпадал бы, и
            React показал бы одну строку вместо двух.
          */}
          {rows.map((finding) => (
            <Row key={`${finding.kind}-${finding.document_id ?? finding.account_id}`} finding={finding} />
          ))}
        </tbody>
      </table>
      <PageBar {...bar} what="находок" />
    </>
  )
}

function Row({ finding }) {
  const burning = finding.urgency === 'burning'
  return (
    // `data-label` - подпись колонки для узкого экрана: там строка
    // разворачивается в карточку, заголовки таблицы не видны, и без подписи
    // человек не знает, что за число перед ним (`styles.adaptive.css`).
    // У первой ячейки подписи нет намеренно: метка срочности читается сама.
    <tr>
      <td>
        <Tag kind={burning ? 'burn' : 'muted'}>{finding.urgency_title}</Tag>
      </td>
      <td data-label="Что нашли">{finding.kind_title}</td>
      {/*
        `table__block`: в ячейке предложение, а не значение. Без него абзацу
        на узком экране остаётся половина ширины карточки, и он складывается в
        столбик по два слова.
      */}
      <td className="table__block" data-label="Почему">
        <span className="muted">{finding.explanation}</span>
      </td>
      {/* Сумма набрана полужирной (указание владельца 22.09.2026): в строке
          из семи значений деньги - главное, и взгляд обязан находить их сам. */}
      <td className="table__num radar__sum" data-label="Сумма">
        {money(finding.amount)}
      </td>
      <td data-label="Счёт и дело">
        <AccountCell finding={finding} />
      </td>
      <td data-label="Срок">
        <span className="muted">{finding.term ?? '-'}</span>
      </td>
      <td data-label="Что делать">
        {/*
          Действие строкой, а не кнопкой: девять залитых кнопок в столбик
          читаются как девять разных дел, между тем дело одно - открыть счёт и
          сделать. Ссылка выглядит ссылкой и подчёркивается при наведении, так
          что правило подъёма (скилл `frontend`, 6б) её не касается.
        */}
        {finding.account_id ? (
          <Link className="radar__act" to={`/accounts/${finding.account_id}`}>
            {finding.action} →
          </Link>
        ) : (
          <span className="muted">{finding.action}</span>
        )}
      </td>
    </tr>
  )
}

/*
  Счёт, адрес и дело приходят с сервера отдельными полями (22.09.2026). До
  этого номер дела существовал только внутри заголовка находки, и вынуть его
  здесь можно было бы лишь разбором предложения - вторым источником правды,
  который разъедется с первым при первой правке формулировки на сервере.
*/
function AccountCell({ finding }) {
  /*
    Дело показывается и тогда, когда счёта нет. Так бывает: находка привязана к
    исполнительному документу, а счёт по нему не заведён. Ранний возврат «-» на
    отсутствующем счёте прятал бы номер дела - единственное, чем такая строка
    вообще опознаётся (поймано прогоном 22.09.2026).
  */
  const account = finding.account_number ? (
    <Link to={`/accounts/${finding.account_id}`}>№ {finding.account_number}</Link>
  ) : null
  const label = finding.case_number ? (
    <span className="radar__case">дело {finding.case_number}</span>
  ) : null

  if (!account && !label) return <span className="muted">-</span>
  return (
    <>
      {account}
      {account && label ? ' · ' : null}
      {label}
      {finding.account_address ? <div className="muted">{finding.account_address}</div> : null}
    </>
  )
}
